import { X } from 'lucide-react'
import { ReactNode, useState } from 'react'
import { useQuery } from 'react-query'

import { ImageContainer } from '@/components'
import { Buttons } from '@/components/common/buttons'
import { IconActionButton } from '@/components/common/buttons/icon-action-button'
import { ConfirmationDialog } from '@/components/common/data-display/ConfirmationDialog'
import { UpdatedAtLabel } from '@/components/common/data-display/updated-label-v2'
import { Can } from '@/features/sessao/providers/auth-context'

type Repo<T> = {
  search: (params: { callId: number }) => Promise<(T & { imageStr: string })[]>
  delete: (params: { id: string }) => Promise<unknown>
}

type Props<T> = {
  callId?: number
  isEditable?: boolean
  queryKey: string
  repository: Repo<T>
  renderCreateDialog: (props: { onSuccess: () => void }) => ReactNode
}

export function EvidenceGallery<T extends { id: string }>({
  callId,
  isEditable,
  queryKey,
  repository,
  renderCreateDialog,
}: Props<T>) {
  const [deleteId, setDeleteId] = useState<string>()

  const { data, isFetching, dataUpdatedAt, refetch } = useQuery(
    [queryKey, callId],
    async () => {
      if (callId) return await repository.search({ callId: Number(callId) })
    },
  )

  const onDelete = async (id: string) => {
    try {
      await repository.delete({ id })
      refetch()
    } catch {
      // Error toast is already shown by the API connector interceptor
    }
  }

  return (
    <div>
      <div className="mb-4">
        <div className="flex items-center gap-2">
          {callId &&
            isEditable &&
            renderCreateDialog({ onSuccess: () => refetch() })}
          <Buttons.Refresh isLoading={isFetching} onClick={() => refetch()} />
        </div>

        <UpdatedAtLabel updatedAt={dataUpdatedAt} />
      </div>

      <div className="flex flex-wrap gap-2">
        {(data ?? []).map((ea) => (
          <div key={ea.id} className="relative">
            <ImageContainer src={ea.imageStr} className="size-20" />

            {isEditable && (
              <Can asDefault I="delete" a="CallReportAttachment">
                <IconActionButton
                  icon={X}
                  tooltip="Excluir"
                  size="sm"
                  className="absolute right-2 top-2"
                  onClick={() => setDeleteId(ea.id)}
                />
              </Can>
            )}
          </div>
        ))}

        {(data ?? []).length === 0 && <p>Galeria vazia</p>}
      </div>

      <ConfirmationDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(undefined)}
        onConfirm={() => {
          if (deleteId) onDelete(deleteId)
          setDeleteId(undefined)
        }}
      >
        Confirma exclusão da evidência?
      </ConfirmationDialog>
    </div>
  )
}
